import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer, PolarRadiusAxis } from "recharts";
import { useIsMobile } from '@/hooks/use-mobile';

interface RadarData {
  skill: string;
  score: number;
}

interface SkillsRadarChartProps {
  data?: RadarData[];
}

// 🔹 Helper: capitalize only the first letter
const capitalizeFirstLetter = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

export const SkillsRadarChart = ({ data }: SkillsRadarChartProps) => {
  const isMobile = useIsMobile();

  const chartData = data
    ? data.map((item) => ({
      subject: item.skill,
      A: item.score,
      fullMark: 100,
    }))
    : [];

  return (
    <div className="w-full h-60 flex items-center justify-center">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart cx="50%" cy="50%" outerRadius={isMobile ? "67%" : "80%"} data={chartData}>
          <PolarGrid stroke="hsl(var(--border))" />
          <PolarRadiusAxis
            domain={[0, 100]}
            angle={0}
            tickCount={5}
            tick={false}
            axisLine={false} />
          <PolarAngleAxis
            dataKey="subject"
            tick={({ payload, x, y, cx, cy }) => {
              const numX = Number(x);
              const numY = Number(y);
              const numCX = Number(cx);
              const numCY = Number(cy);

              const labelOffset = isMobile ? 5 : 8;
              const dx = numX - numCX;
              const dy = numY - numCY;
              const len = Math.sqrt(dx * dx + dy * dy);

              let newX = numCX + (dx / len) * (len + labelOffset);
              let newY = numCY + (dy / len) * (len + labelOffset);

              // value from backend (lowercase)
              let label = capitalizeFirstLetter(payload.value);
              if (isMobile && payload.value === "comprehension") {
                label = "Compr.";
              }

              // Desktop-specific label positioning
              if (!isMobile && payload.value === "pronunciation") {
                newX -= 35;
                newY += -2;
              }
              if (!isMobile && payload.value === "grammar") {
                newX -= -20;
                newY += -2;
              }
              if (!isMobile && payload.value === "fluency") {
                newX -= 1;
                newY += 0;
              }
              if (!isMobile && payload.value === "vocabulary") {
                newX -= -30;
                newY += 0;
              }
              if (!isMobile && payload.value === "comprehension") {
                newX -= 44;
                newY += 0;
              }

              // Mobile-specific label positioning to prevent overlap
              if (isMobile && payload.value === "comprehension") {
                newX -= 25;
                newY += -4;
              }
              if (isMobile && payload.value === "grammar") {
                newX += 25;
                newY += -4;
              }
              if (isMobile && payload.value === "fluency") {
                newY -= 5;
              }
              if (isMobile && payload.value === "pronunciation") {
                newX -= 40;
                newY += 4;
              }
              if (isMobile && payload.value === "vocabulary") {
                newX += 35;
                newY += 4;
              }

              if (!isMobile) {
                return (
                  <text
                    x={newX}
                    y={newY}
                    dy={4}
                    textAnchor="middle"
                    fill="#6B7280"
                    fontFamily="Inter, sans-serif"
                    fontWeight={400}
                    fontSize={13}
                    style={{ lineHeight: "100%", fontSize: '13px', fontWeight: 400, color: '#6B7280', letterSpacing: '0%' }}
                  >
                    {label}
                  </text>
                );
              }
              
              const rotations: Record<string, number> = {
                fluency: 0,
                grammar: 0,
                vocabulary: 0,
                pronunciation: 0,
                comprehension: 0,
              };
              const rotation = rotations[payload.value] ?? -10;

              return (
                <g transform={`translate(${newX},${newY})`}>
                  <text
                    x={0}
                    y={0}
                    dy={4}
                    textAnchor="middle"
                    fill="#6B7280"
                    fontFamily="Inter, sans-serif"
                    fontWeight={400}
                    fontSize={13}
                    transform={`rotate(${rotation})`}
                  >
                    {label}
                  </text>
                </g>
              );
            }}
          />

          <Radar
            name="Skills"
            dataKey="A"
            stroke="hsl(var(--primary))"
            fill="hsl(var(--primary))"
            fillOpacity={0.3}
            strokeWidth={2}
            isAnimationActive={true}
            animationDuration={400}
            animationEasing="ease-out"
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};
